import { useContext, useMemo, useRef } from "react";
import { useSyncExternalStoreWithSelector } from "use-sync-external-store/shim/with-selector";
import { dequal } from "dequal";

import { useDeepCompareEffect } from "@smartrent/hooks";

import type { Context } from "react";

export type StoreProvider<T> = {
  subscribe: (listener: VoidFunction) => VoidFunction;
  getSnapshot: () => T;
};

export class Store<T> {
  private value: T;
  private listeners: Set<VoidFunction> = new Set<VoidFunction>();

  constructor(defaultValue: T) {
    this.value = defaultValue;
  }

  getSnapshot = (): T => {
    return this.value;
  };

  subscribe = (listener: VoidFunction) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  notify = () => {
    this.listeners.forEach((listener) => listener());
  };

  setValue = (value: T, notify = true) => {
    this.value = value;
    if (notify) {
      this.notify();
    }
  };
}

export function useProviderStore<T>(value: T) {
  const store = useRef<Store<T>>(new Store(value)).current;

  const providerValue = useMemo(() => {
    return {
      getSnapshot: store.getSnapshot,
      subscribe: store.subscribe,
    };
  }, [store.getSnapshot, store.subscribe]);

  useDeepCompareEffect(() => {
    store.setValue(value);
  }, [value]);

  return {
    store,
    value: providerValue,
  };
}

export type SelectorFn<TState, TSelected> = (state: TState) => TSelected;

export function useContextSelector<TState, TSelected>(
  context: Context<StoreProvider<TState>>,
  selectorFn: SelectorFn<TState, TSelected>
): TSelected {
  const providerValue = useContext<StoreProvider<TState>>(context);

  if (!providerValue) {
    throw new Error("useContextSelector must be used within a StoreProvider");
  }

  const { subscribe, getSnapshot } = providerValue;

  return useSyncExternalStoreWithSelector(
    subscribe,
    getSnapshot,
    null,
    selectorFn,
    dequal
  );
}
